import {ChangeDetectionStrategy, Component, DestroyRef, ElementRef, HostListener, Inject, inject, OnInit, PLATFORM_ID, ViewChild} from '@angular/core';
import {FormsModule} from "@angular/forms";
import {RecommendationListComponent} from "./recommendations/recommendation-list/recommendation-list.component";
import {DayInformation} from "../shared/models/day-information.model";
import {DayInformationService} from "../services/day-information.service";
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {ActivatedRoute, NavigationStart, Params, Router} from "@angular/router";
import {TitleService} from "../services/title.service";
import {MetaService} from "../services/meta.service";
import {MatDialog} from "@angular/material/dialog";
import {ErrorService} from "../core/error/error.service";
import {DayFilterComponent} from "./day-filter/day-filter.component";
import {getAllCategories} from "../core/utils/task.utils";
import {DayPipe} from "../core/utils/pipe.utils";
import {MatFormField, MatLabel, MatSuffix} from "@angular/material/form-field";
import {MatIcon} from "@angular/material/icon";
import {formatDate, isPlatformBrowser, NgClass} from "@angular/common";
import {ErrorComponent} from "../core/error/error.component";
import {Task} from "../shared/models/task.model";
import {MatIconButton} from "@angular/material/button";
import {MatInput} from "@angular/material/input";
import {InformationComponent} from "./information/information.component";
import {MatNativeDateModule} from "@angular/material/core";
import {routeDateFormat} from "../core/constants/route.formats";
import {filter} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-day',
    standalone: true,
    templateUrl: './day.component.html',
    imports: [
        FormsModule,
        InformationComponent,
        MatFormField,
        MatLabel,
        MatSuffix,
        TranslatePipe,
        MatIcon,
        RecommendationListComponent,
        NgClass,
        ErrorComponent,
        MatIconButton,
        MatInput,
        MatNativeDateModule
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./day.component.scss']
})
export class DayComponent implements OnInit {

    @ViewChild(RecommendationListComponent) child: RecommendationListComponent | undefined;
    @ViewChild('filterBtnRef', {static: false}) public filterBtnRef: ElementRef | undefined;

    windowScrolled: boolean = false;

    currentDay: Date = new Date();

    searchText: string = "";

    dayInformation: DayInformation | undefined;
    allTasks = new Array<Task>();

    error: boolean = false;

    private readonly destroyRef = inject(DestroyRef);
    private readonly isBrowser: boolean;
    private dayInformationLoaded = false;
    private allTasksLoaded = false;
    private scrollPositionToRestore = 0;

    constructor(
        private dayService: DayInformationService,
        private translate: TranslateService,
        private route: ActivatedRoute,
        private titleService: TitleService,
        private metaService: MetaService,
        private dialog: MatDialog,
        private errorService: ErrorService,
        private router: Router,
        @Inject(PLATFORM_ID) platformId: Object
    ) {
        this.isBrowser = isPlatformBrowser(platformId);
    }

    ngOnInit(): void {
        this.restoreScrollPositionFromHistory();
        this.saveScrollPositionBeforeDetailNavigation();

        this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
            this.initRouteDay(params);
            this.fetchDayInformation();
        });

        this.errorService.getError().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(error => {
            this.error = error;
        })

        this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(_ => {
            this.fetchDayInformation();
        })
    }


    fetchDayInformation() {
        if (!this.currentDay) {
            return;
        }

        this.dayInformationLoaded = false;
        this.allTasksLoaded = false;

        this.dayService.getDayInformation(this.currentDay.getTime()).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((dayInformation: DayInformation) => {
            this.dayInformation = dayInformation;
            this.dayInformation.day = this.currentDay;
            this.setDayInformationMeta(dayInformation);
            this.dayInformationLoaded = true;
            this.restoreScrollPosition();
        });

        this.dayService.getAllTasks().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((tasks: Task[]) => {
            this.allTasks = tasks;
            this.allTasksLoaded = true;
            this.restoreScrollPosition();
        });
    }

    initRouteDay(params: Params) {
        const routeDay = params['currentDay'];
        if (routeDay) {
            this.currentDay = new Date(routeDay);

            this.setTitleAndMeta();
        }
    }

    @HostListener("window:scroll", [])
    onWindowScroll() {
        if (window.scrollY || document.documentElement.scrollTop || document.body.scrollTop > 100) {
            this.windowScrolled = true;
        } else if (this.windowScrolled && window.scrollY || document.documentElement.scrollTop || document.body.scrollTop < 10) {
            this.windowScrolled = false;
        }
    }

    scrollToTop() {
        window.scrollTo(0, 0)
    }

    private restoreScrollPositionFromHistory() {
        if (!this.isBrowser) {
            return;
        }

        const scrollPosition = history.state?.['dayScrollPosition'];
        this.scrollPositionToRestore = typeof scrollPosition === 'number' ? scrollPosition : 0;
    }

    private saveScrollPositionBeforeDetailNavigation() {
        if (!this.isBrowser) {
            return;
        }

        this.router.events.pipe(
            filter((event): event is NavigationStart => event instanceof NavigationStart),
            filter(event => /^\/(en\/)?details\//.test(event.url)),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(() => {
            history.replaceState({...history.state, dayScrollPosition: window.scrollY}, '');
        });
    }

    private restoreScrollPosition() {
        if (!this.isBrowser || !this.dayInformationLoaded || !this.allTasksLoaded || this.scrollPositionToRestore <= 0) {
            return;
        }

        const scrollPosition = this.scrollPositionToRestore;
        this.scrollPositionToRestore = 0;

        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => window.scrollTo(0, scrollPosition));
        });
    }

    openFilterDialog() {
        if (!this.filterBtnRef ||!this.dayInformation) {
            return;
        }

        const allCategories = getAllCategories(this.dayInformation)

        const dialogRef = this.dialog.open(DayFilterComponent, {
            data: allCategories,
            width: 'min(460px, 92vw)',
            maxWidth: '92vw'
        });

        dialogRef.afterClosed().subscribe(_ => {
            if (!this.child) {
                return
            }

            this.child.initTasks()
        });
    }

    private setTitleAndMeta() {
        const dayTransformed = new DayPipe(this.translate).transform(this.currentDay);
        const path = `/day/${formatDate(this.currentDay, routeDateFormat, 'en')}`;

        this.metaService.updateCanonicalUrl(path);

        this.translate.get('TITLE.DAY_INFO', {date: dayTransformed}).subscribe((translated: string) => {
            this.titleService.updateTitleText(this.translate, translated);
        });

        this.translate.get('META.DESCRIPTION.DAY').subscribe((translated: string) => {
            this.metaService.updateDescription(translated);
            this.metaService.updateSocialTags(
                this.translate.instant('TITLE.DAY_INFO', {date: dayTransformed}),
                translated,
                path
            );
        });

        this.translate.get('META.KEYWORDS.DAY', {date: dayTransformed}).subscribe((translated: string) => {
            this.metaService.updateKeywords(translated);
        });
    }

    private setDayInformationMeta(dayInformation: DayInformation) {
        const dayTransformed = new DayPipe(this.translate).transform(this.currentDay);
        const phase = this.translate.instant(`PHASE.${dayInformation.phase}`);
        const sign = this.translate.instant(`SIGN.${dayInformation.sign.id.toUpperCase()}`);
        const title = this.translate.instant('TITLE.DAY_INFO', {date: dayTransformed});
        const path = `/day/${formatDate(this.currentDay, routeDateFormat, 'en')}`;
        const description = `${this.translate.instant('META.DESCRIPTION.DAY')} ${phase}, ${sign}.`;

        this.titleService.updateTitleText(this.translate, title);
        this.metaService.updateDescription(description);
        this.metaService.updateSocialTags(title, description, path);
    }
}
